import React, { useEffect, useMemo, FC } from "react";
import { Popover, Paper, Box, Button, Typography, Divider, IconButton } from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import FilterDropdown, { type FilterOption, type FilterOptionValue } from "./FilterDropdown";
import { useGetJurisdictionsListQuery } from "../../../redux/reducers/jurisdictions.reducer";
import { useGetViolationsListQuery } from "../../../redux/reducers/settings.reducer";
import { useGetInterceptorsListQuery } from "../../../redux/reducers/configuration.reducer";
import { JURISDICTION_TYPE_E } from "../../../types/jurisdictions.types";
import { EVENT_PRODUCT_TYPE_E } from "../../../types";
import { convertSnakeToTitleCase } from "../../../utils/commonUtils";
import { NON_HSRP_VIOLATION_NAMES } from "../../../utils/constants";

interface IFilterPopupProps {
	anchorEl: HTMLElement | null;
	isOpen: boolean;
	onClose: () => void;
	selectedRegions: FilterOptionValue[];
	setSelectedRegions: React.Dispatch<React.SetStateAction<FilterOptionValue[]>>;
	selectedViolations: FilterOptionValue[];
	setSelectedViolations: React.Dispatch<React.SetStateAction<FilterOptionValue[]>>;
	selectedInterceptors: FilterOptionValue[];
	setSelectedInterceptors: React.Dispatch<React.SetStateAction<FilterOptionValue[]>>;
}

const FilterPopup: FC<IFilterPopupProps> = ({
	anchorEl,
	isOpen,
	onClose,
	selectedRegions,
	setSelectedRegions,
	selectedViolations,
	setSelectedViolations,
	selectedInterceptors,
	setSelectedInterceptors
}) => {
	// API calls
	const { data: jurisdictionsResponse } = useGetJurisdictionsListQuery({
		all: true,
		jurisdiction_type: JURISDICTION_TYPE_E.JURISDICTION
	});

	const { data: violationsResponse } = useGetViolationsListQuery();

	const { data: interceptorsResponse } = useGetInterceptorsListQuery({ page: 1, all: true });

	// Transform jurisdictions to filter options
	const allRegions = useMemo<FilterOption[]>(() => {
		if (!jurisdictionsResponse?.results) return [];
		return jurisdictionsResponse.results.map((jurisdiction) => ({
			value: jurisdiction.id,
			label: jurisdiction.name
		}));
	}, [jurisdictionsResponse]);

	// Transform violations to filter options
	const allViolations = useMemo<FilterOption[]>(() => {
		if (!violationsResponse) return [];
		return violationsResponse
			.filter((violation) => violation.product_type === EVENT_PRODUCT_TYPE_E.RECON)
			.map((violation) => ({
				value: violation.id,
				label: NON_HSRP_VIOLATION_NAMES.includes(violation.name) ? "Non HSRP" : convertSnakeToTitleCase(violation.name)
			}));
	}, [violationsResponse]);

	// Transform interceptors to filter options and create jurisdiction mapping
	const { allInterceptors, interceptorJurisdictionMap } = useMemo<{
		allInterceptors: FilterOption[];
		interceptorJurisdictionMap: Map<FilterOptionValue, number>;
	}>(() => {
		const interceptors: FilterOption[] = [];
		const map = new Map<FilterOptionValue, number>();

		if (interceptorsResponse?.results) {
			interceptorsResponse.results.forEach((interceptor) => {
				const jurisdictionId = interceptor.device_data?.jurisdiction;
				if (jurisdictionId) {
					map.set(interceptor.id, jurisdictionId);
				}
				interceptors.push({
					value: interceptor.id,
					label: interceptor.numberplate || `Interceptor ${interceptor.id}`
				});
			});
		}

		return { allInterceptors: interceptors, interceptorJurisdictionMap: map };
	}, [interceptorsResponse]);

	// Get filtered interceptors based on selected regions (including descendants)
	const availableInterceptors = useMemo<FilterOption[]>(() => {
		if (selectedRegions.length === 0) return [];

		// Create a set of all valid jurisdiction IDs (selected regions + their descendants)
		const validJurisdictionIds = new Set<FilterOptionValue>();

		selectedRegions.forEach((regionId) => {
			// Add the selected region itself
			validJurisdictionIds.add(regionId);

			// Find the jurisdiction in the response and add its descendants
			const jurisdiction = jurisdictionsResponse?.results?.find((j) => j.id === regionId);
			if (jurisdiction?.descendants) {
				jurisdiction.descendants.forEach((descendant) => {
					validJurisdictionIds.add(descendant.id);
				});
			}
		});

		return allInterceptors.filter((interceptor) => {
			const interceptorValue = typeof interceptor === "string" ? interceptor : interceptor.value;
			if (!interceptorValue) return false;
			const interceptorJurisdiction = interceptorJurisdictionMap.get(interceptorValue as FilterOptionValue);
			return interceptorJurisdiction && validJurisdictionIds.has(interceptorJurisdiction);
		});
	}, [selectedRegions, allInterceptors, interceptorJurisdictionMap, jurisdictionsResponse]);

	// Utility function to toggle selection
	const toggleSelection = (
		value: FilterOptionValue,
		currentSelection: FilterOptionValue[],
		setSelection: React.Dispatch<React.SetStateAction<FilterOptionValue[]>>
	) => {
		setSelection((prev) => {
			if (prev.includes(value)) {
				return prev.filter((item) => item !== value);
			}
			return [...prev, value];
		});
	};

	const handleClearAll = () => {
		setSelectedRegions([]);
		setSelectedViolations([]);
		setSelectedInterceptors([]);
	};

	// Remove interceptors that are no longer available when region changes
	useEffect(() => {
		if (selectedInterceptors.length > 0) {
			const availableInterceptorValues = availableInterceptors.map((opt) =>
				typeof opt === "string" ? opt : opt.value ?? String(opt)
			) as FilterOptionValue[];

			if (availableInterceptorValues.length > 0) {
				const validInterceptors = selectedInterceptors.filter((interceptor) =>
					availableInterceptorValues.includes(interceptor)
				);
				if (validInterceptors.length !== selectedInterceptors.length) {
					setSelectedInterceptors(validInterceptors);
				}
			} else if (selectedRegions.length === 0) {
				// Clear interceptors if no regions selected
				setSelectedInterceptors([]);
			}
		}
	}, [selectedRegions, availableInterceptors, selectedInterceptors, setSelectedInterceptors]);

	// Handle Select All for Regions
	const handleSelectAllRegions = (filteredOptions: FilterOption[]) => {
		const allValues = filteredOptions.map((opt) =>
			typeof opt === "string" ? opt : opt.value ?? String(opt)
		) as FilterOptionValue[];
		const allSelected = allValues.every((val) => selectedRegions.includes(val));

		if (allSelected) {
			// Deselect all filtered options
			setSelectedRegions(selectedRegions.filter((region) => !allValues.includes(region)));
		} else {
			// Select all filtered options
			const newSelections = Array.from(new Set([...selectedRegions, ...allValues]));
			setSelectedRegions(newSelections);
		}
	};

	// Handle Select All for Interceptors
	const handleSelectAllInterceptors = (filteredOptions: FilterOption[]) => {
		const allValues = filteredOptions.map((opt) =>
			typeof opt === "string" ? opt : opt.value ?? String(opt)
		) as FilterOptionValue[];
		const allSelected = allValues.every((val) => selectedInterceptors.includes(val));

		if (allSelected) {
			// Deselect all filtered options
			setSelectedInterceptors(selectedInterceptors.filter((interceptor) => !allValues.includes(interceptor)));
		} else {
			// Select all filtered options
			const newSelections = Array.from(new Set([...selectedInterceptors, ...allValues]));
			setSelectedInterceptors(newSelections);
		}
	};

	// Handle Select All for Violations
	const handleSelectAllViolations = (filteredOptions: FilterOption[]) => {
		const allValues = filteredOptions.map((opt) =>
			typeof opt === "string" ? opt : opt.value ?? String(opt)
		) as FilterOptionValue[];
		const allSelected = allValues.every((val) => selectedViolations.includes(val));

		if (allSelected) {
			// Deselect all filtered options
			setSelectedViolations(selectedViolations.filter((violation) => !allValues.includes(violation)));
		} else {
			// Select all filtered options
			const newSelections = Array.from(new Set([...selectedViolations, ...allValues]));
			setSelectedViolations(newSelections);
		}
	};

	return (
		<Popover
			open={isOpen}
			anchorEl={anchorEl}
			onClose={onClose}
			anchorOrigin={{
				vertical: "bottom",
				horizontal: "left"
			}}
			transformOrigin={{
				vertical: "top",
				horizontal: "left"
			}}
			PaperProps={{
				sx: {
					mt: 1,
					minWidth: 450,
					maxWidth: "90vw",
					borderRadius: 2,
					boxShadow: "0 4px 20px rgba(0,0,0,0.15)"
				}
			}}
		>
			<Paper>
				<Box sx={{ p: 3 }}>
					<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
						<Typography variant="h6" fontWeight={600} sx={{ color: "text.primary" }}>
							Filter Options
						</Typography>
						<IconButton
							onClick={onClose}
							size="small"
							sx={{
								color: "text.secondary",
								"&:hover": {
									bgcolor: "action.hover",
									color: "text.primary"
								}
							}}
						>
							<CloseIcon fontSize="small" />
						</IconButton>
					</Box>

					<Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
						{/* Region - First */}
						<FilterDropdown
							label="Region"
							placeholder="Select regions..."
							options={allRegions}
							selectedItems={selectedRegions}
							onToggleSelection={(value) => toggleSelection(value, selectedRegions, setSelectedRegions)}
							onSelectAll={handleSelectAllRegions}
						/>

						{/* Interceptor ID - Second */}
						<FilterDropdown
							label="Interceptor ID"
							placeholder={selectedRegions.length > 0 ? "Select interceptors..." : "Select regions first..."}
							options={availableInterceptors}
							selectedItems={selectedInterceptors}
							onToggleSelection={(value) => toggleSelection(value, selectedInterceptors, setSelectedInterceptors)}
							disabled={selectedRegions.length === 0}
							onSelectAll={handleSelectAllInterceptors}
						/>

						{/* Violation Type - Third */}
						<FilterDropdown
							label="Violation Type"
							placeholder="Select violations..."
							options={allViolations}
							selectedItems={selectedViolations}
							onToggleSelection={(value) => toggleSelection(value, selectedViolations, setSelectedViolations)}
							displayKey="label"
							onSelectAll={handleSelectAllViolations}
						/>
					</Box>
				</Box>

				<Divider />

				<Box sx={{ p: 2, display: "flex", justifyContent: "space-between", gap: 2 }}>
					<Button
						onClick={handleClearAll}
						sx={{
							textTransform: "none",
							color: "text.secondary",
							"&:hover": {
								bgcolor: "action.hover"
							}
						}}
					>
						Clear All
					</Button>
					<Button
						onClick={onClose}
						variant="contained"
						sx={{
							textTransform: "none",
							bgcolor: "primary.main",
							"&:hover": { bgcolor: "primary.dark" }
						}}
					>
						Apply Filters
					</Button>
				</Box>
			</Paper>
		</Popover>
	);
};

export default FilterPopup;
